import React, { useMemo } from "react";
import classNames from "classnames";
import Markdown from "markdown-to-jsx";

import { DivideSection } from "../../common/DivideSection";
import { NormalSection } from "../../common/NormalSection";
import { Title } from "../../common/Title";
import styles from "./Venue.module.scss";
import { SocialsList } from "../../common/SocialsList";
import { OpenLabel, OpenStatus } from "../../common/OpenLabel";
import { FramedImage } from "../../common/FramedImage";

import { TwitterFeed } from "../../common/Twitter/Feed";
import { InstagramFeed } from "../../common/Instagram/Feed";
import { EventItem } from "../../common/Event";
import { CreativeMarkdown } from "../../common/CreativeMarkdown";
import { LegalFooter } from "../../Layout/LegalFooter";

const TEST_EVENT = {
  title: "Evening Classes ",
  time: "Weekdays",
  description:
    "Local residents are entitled to a 20% discount on all evening classes through the ‘Better Bankside’ scheme",
};

export enum ContactType {
  Telephone = "telephone",
  Email = "email",
  Link = "link",
}

export enum SocialType {
  Twitter = "twitter",
  Instagram = "instagram",
  Facebook = "facebook",
  Website = "website",
}

export interface EventFragment {
  specific: string;
  title: string;
  subtitle?: string;
  earliest: string;
  description: string;
  image?: {
    publicURL: string;
  };
}

export interface VenueDataFragment {
  contact: Array<{
    type: ContactType;
    content: string;
    label?: string;
  }>;
  description: string;
  image: {
    publicURL: string;
  };
  information: string;
  instagram?: string;
  name: string;
  social: Array<{
    type: SocialType;
    url: string;
  }>;
  status: OpenStatus;
  status_description: string;
  twitter?: string;
  tags: string[];
  events: EventFragment[];
  location: {
    type: "Point";
    coordinates: mapboxgl.LngLatLike;
  };
}

const getContactProps = (type: ContactType, content: string) => {
  switch (type) {
    case ContactType.Email:
      return {
        href: `mailto:${content}`,
      };
    case ContactType.Telephone:
      return {
        href: `tel:${content}`,
      };
    case ContactType.Link:
    default:
      return {
        href: content,
        target: "_blank",
        rel: "noreferrer noopener",
      };
  }
};

const Venue: React.FC<VenueDataFragment> = ({
  name,
  image,
  description,
  status,
  status_description,
  instagram,
  twitter,
  information,
  contact,
  social,
  events,
}) => {
  const eventImage = useMemo(() => {
    if (events.length === 0) {
      return false;
    }

    return events[0].image;
  }, [events]);
  return (
    <>
      <DivideSection
        clipped
        left={<FramedImage src={image.publicURL} overlay pathName="RoundOne" />}
        right={
          <>
            <Title size="xlarge" tagName="h1" light>
              {name}
            </Title>
            <CreativeMarkdown>{description}</CreativeMarkdown>
          </>
        }
      />
      <DivideSection
        clipped
        left={
          <>
            <Title size="large" tagName="h2">
              Can I Visit now?
            </Title>
          </>
        }
        right={
          <>
            <OpenLabel status={status} />
            <CreativeMarkdown options={{ forceBlock: true }}>
              {status_description}
            </CreativeMarkdown>
          </>
        }
      />
      {events.length > 0 && (
        <DivideSection
          clipped
          left={
            <>
              <Title size="large" tagName="h2">
                What's on?
              </Title>
              {eventImage && (
                <FramedImage src={eventImage.publicURL} pathName="RoundOne" />
              )}
            </>
          }
          right={
            <>
              {events.map((item, idx) => (
                <EventItem key={item.specific} {...item} />
              ))}
            </>
          }
        />
      )}
      {twitter && (
        <DivideSection
          left={
            <>
              <Title tagName="h2" size="small">
                Twitter
              </Title>
              <p>
                Keep up to date with this venue,
                <br />
                through what they are currently posting
              </p>
            </>
          }
          right={
            <TwitterFeed className={styles.twitterFeed} handle={twitter} />
          }
        />
      )}
      <NormalSection className={styles.footer}>
        <div className={classNames(styles.column, styles.columnInfo)}>
          <Title tagName="h3" size="small">
            Venue Information
          </Title>
          <CreativeMarkdown options={{ forceBlock: true }}>
            {information}
          </CreativeMarkdown>
        </div>
        <div className={classNames(styles.column, styles.columnContact)}>
          {contact.length > 0 && (
            <Title tagName="h3" size="small">
              Contact
            </Title>
          )}
          <p>
            {contact.map(item => (
              <a
                key={item.content}
                {...getContactProps(item.type, item.content)}
              >
                {item.label || item.content}
              </a>
            ))}
          </p>
        </div>
        <div className={classNames(styles.column, styles.columnSocial)}>
          <Title tagName="h3" size="small">
            Socials
          </Title>
          <SocialsList items={social.map(item => item.url)} />
        </div>
      </NormalSection>

      <LegalFooter />
    </>
  );
};

export { Venue };
